import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import AppSnackbar from '../../../common/AppSnackbar';
import { useDebounce } from '../../../common/Debounce.hooks';
import { useFindUsers } from '../../../user/User.hooks';
import { User } from '../../../user/User.interface';
import { useHistory } from 'react-router-dom';
import { useUpdateHazard } from '../Hazard.hooks';
import { Hazard } from '../Hazard.interface';

const defaultLimit = 5;

export interface AssignPeopleDialogProps {
  hazardId: number;
  open: boolean;
  assignedUser: User | undefined;
  role: 'Tech Lead' | 'Hazard Owner' | undefined;
  onCancel?: () => void;
  onAssigned: (hazard: Hazard) => void;
}

const HazardAssignPeopleDialog: React.FC<AssignPeopleDialogProps> = ({
  hazardId,
  open,
  role,
  assignedUser,
  onCancel,
  onAssigned
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const debouncedSearch = useDebounce(search || '', 300);
  const previousSearch = useRef('');
  const { userList, isLoading, error, get } = useFindUsers();

  const {
    updatedHazard: assignedUsers,
    isUpdating: isAssigning,
    updateError: assignError,
    updateHazard
  } = useUpdateHazard(hazardId);

  useEffect(() => {
    if (userList) {
      setSelectedUsers(assignedUser ? [assignedUser] : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userList]);

  useEffect(() => {
    // handle searching
    if (debouncedSearch !== previousSearch.current) {
      previousSearch.current = debouncedSearch;
      setPage(0);
    }
    const params = {
      filter: debouncedSearch,
      page,
      limit
    } as any;
    get(params);
  }, [debouncedSearch, get, limit, page]);

  useEffect(() => {
    // effect after users assigned to group
    if (assignedUsers) {
      onAssigned(assignedUsers);
    }
  }, [assignedUsers, onAssigned]);

  const isSelected = useCallback(
    (user: User) => {
      // check if a user is selected
      return selectedUsers.some(u => u.id === user.id);
    },
    [selectedUsers]
  );
  const filteredUsers = useMemo(() => {
    // remove any selected users since we display selected users above the
    // user list
    if (!userList) return [];
    return userList.data.filter(user => !isSelected(user));
  }, [isSelected, userList]);

  const onAssign = () => {
    const user = selectedUsers.map(u => u.id)[0];

    updateHazard(
      role === 'Hazard Owner'
        ? { hazardOwnerId: user }
        : {
            techLeadId: user
          }
    );
  };

  const renderRows = (users: User[]) => {
    return users.map(user => (
      <TableRow
        key={user.id}
        hover
        role='checkbox'
        color='primary'
        selected={isSelected(user)}
        onClick={() => {
          if (isSelected(user)) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
          } else {
            setSelectedUsers([user]);
          }
        }}
      >
        <TableCell padding='checkbox'>
          <Checkbox color='primary' checked={isSelected(user)} />
        </TableCell>
        <TableCell>{user.firstName}</TableCell>
        <TableCell>{user.lastName}</TableCell>
        <TableCell>
          {user.groups?.map(group => group.name).join(', ')}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Assign {role}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Search for people that you would like to assign.
        </DialogContentText>
        <Box mt={1} mb={2}>
          <form autoComplete='off' onSubmit={event => event.preventDefault()}>
            <TextField
              variant='outlined'
              autoFocus
              fullWidth
              autoComplete='off'
              type='text'
              name='name'
              label='Name'
              onChange={event => setSearch(event.target.value)}
            />
          </form>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={3} padding='none'>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
                {renderRows(selectedUsers)}
                {renderRows(filteredUsers)}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component='div'
            count={userList?.total || 0}
            rowsPerPage={limit}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={event => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
          <AppSnackbar severity='error' open={Boolean(error)}>
            There was an error loading the list of users.
          </AppSnackbar>
        </Card>
        {assignError && (
          <Alert severity='error'>
            Error occurred.{' '}
            {assignError?.message ||
              'Please check your internet connection and try again.'}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          color='primary'
          disabled={isLoading || isAssigning || selectedUsers.length <= 0}
          endIcon={
            isAssigning && <CircularProgress color='primary' size={20} />
          }
          onClick={onAssign}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HazardAssignPeopleDialog;
